// Microsoft variant of the Dormant DDA Actions row. Scoped specifically to Active goals on
// DataDriven attribution that contribute to bidding (ExcludeFromBidding=false) — LastClick /
// LinearTouch / etc. don't have a stale-model dormancy concern, so they're filtered out
// before reaching this renderer. The Google version's three-tier DDA health ladder (expired
// / stale / slipping) collapses to firing-vs-dormant on Microsoft (binary tracking signal).

import type { EvidenceProps } from './registry';

interface GoalRow {
  id?: string;
  name?: string;
  type?: string;
  scope?: string;
  status?: string;
  excludeFromBidding: boolean;
  trackingStatus?: string;
  attributionModelType?: string;
  isDormantBidder: boolean;
}

interface DormantMsShape {
  totalActiveContributing: number;
  dormantCount: number;
  firingCount: number;
  goals: GoalRow[];
}

function formatTracking(t: string | undefined): string {
  if (!t) return '—';
  if (t === 'RecordingConversions') return 'Firing';
  if (t === 'NoRecentConversions') return 'Dormant';
  if (t === 'NotTracking') return 'Not tracking';
  return t;
}

export const DormantBiddingGoalsMicrosoftEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as DormantMsShape;
  const noIssues = e.dormantCount === 0;

  return (
    <div className='evidence'>
      <p className='evidence__lead'>
        {noIssues ? (
          <>
            All {e.totalActiveContributing} Active goal{e.totalActiveContributing === 1 ? '' : 's'}{' '}
            on <strong>Data-Driven Attribution</strong> contributing to bidding are firing — DDA
            models have the volume they need.
          </>
        ) : (
          <>
            <strong>{e.dormantCount}</strong> of {e.totalActiveContributing} Active goal
            {e.totalActiveContributing === 1 ? '' : 's'} on <strong>Data-Driven Attribution</strong>{' '}
            contributing to bidding {e.dormantCount === 1 ? 'is' : 'are'} dormant. DDA needs
            continuous volume to function — when its goals stop firing, bidding decisions flow
            through an attribution model that&apos;s starved of signal. Microsoft gives a binary
            tracking signal, so we surface dormancy without an exact volume threshold.
          </>
        )}
      </p>

      <div className='dda-health-summary'>
        <div className='dda-health-summary__cell dda-health-summary__cell--stale'>
          <div className='dda-health-summary__num'>{e.dormantCount.toLocaleString()}</div>
          <div className='dda-health-summary__lbl'>Dormant</div>
        </div>
        <div className='dda-health-summary__cell dda-health-summary__cell--healthy'>
          <div className='dda-health-summary__num'>{e.firingCount.toLocaleString()}</div>
          <div className='dda-health-summary__lbl'>Firing</div>
        </div>
      </div>

      <table className='evidence__table evidence__table--dormant-ms'>
        <thead>
          <tr>
            <th>Goal name</th>
            <th>Type</th>
            <th>Attribution</th>
            <th>Tracking</th>
          </tr>
        </thead>
        <tbody>
          {e.goals.map(r => (
            <tr
              key={r.id ?? r.name}
              className={r.isDormantBidder ? 'evidence__row--flagged' : undefined}
            >
              <td>
                <div>{r.name ?? '(unnamed)'}</div>
                {r.id && <div className='evidence__action-id'>{r.id}</div>}
              </td>
              <td>{r.type ?? '—'}</td>
              <td>{r.attributionModelType ?? '—'}</td>
              <td>
                <span
                  className={`evidence__pill ${
                    r.isDormantBidder
                      ? 'evidence__pill--health-stale'
                      : 'evidence__pill--health-healthy'
                  }`}
                >
                  {formatTracking(r.trackingStatus)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className='evidence__footnote'>
        Goals shown: <code>Active</code> + contributing (<code>ExcludeFromBidding=false</code>) +{' '}
        <code>AttributionModelType=DataDriven</code>. Other attribution models (LastClick,
        LinearTouch, etc.) don&apos;t have a stale-model dormancy concern and are excluded from this
        row. Tracking signal is categorical (binary) — magnitude data requires Microsoft&apos;s
        async Reporting API, deferred from this slice.
      </p>
    </div>
  );
};
